"use client"
import React, { useState } from "react"
import { motion } from "motion/react"
import { cn } from "@/lib/utils"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel"
import Autoplay from "embla-carousel-autoplay"
import { SectionTitle } from "@/components/ui/section-title"
import { Button } from "@/components/ui/button"
import { navigateToContact } from "@/lib/scroll-utils"
import { useDialog } from "@/contexts/dialog-context"

export default function Testimonials(){
    const [api, setApi] = useState<CarouselApi>()
    const [current, setCurrent] = useState(0)
    const { openDialog } = useDialog()
    
    React.useEffect(() => {
        if (!api) {
            return
        }
        
        setCurrent(api.selectedScrollSnap())
        
        api.on("select", () => {
            setCurrent(api.selectedScrollSnap())
        })
    }, [api])
    
    return(
        <section id="testimonials" className="wrapper bg-foreground overflow-hidden py-medium lg:py-large">
            
                <motion.span className="absolute text-background font-extrabold uppercase text-[6.5vw] whitespace-nowrap -top-[3.5vw] -left-[0.25vw] opacity-10"
                initial={{
                    y: -80
                }}
                whileInView={{
                    y: 0,
                    transition: {
                        duration: 0.3
                    }
                }}>
                    HEAR FROM OUR CUSTOMERS
                </motion.span>
            
            <div className="flex flex-col w-full items-center gap-4">
                <SectionTitle theme="light">
                    Our Testimonials
                </SectionTitle>
                <h2 className="text-background text-center max-w-3xl text-balance">
                    This Is What Selling Your Watch Should Feel Like
                </h2>
                <p className="text-background text-center  max-w-[400px] text-pretty">
                    From London to Edinburgh, watch owners trust us with their treasured timepieces. Discover why.                
                </p>
                <div className="flex justify-center w-full gap-2 tracking wide">
                    <Button 
                        onClick={openDialog}
                        className="bg-background text-brand hover:bg-foreground hover:text-background border border-background"
                    >
                        Sell my watch
                    </Button>

                    <Button 
                        onClick={navigateToContact}
                        className="border border-white text-background bg-transparent"
                    >
                        Get in touch
                    </Button>
            
                </div>
            </div>
            {/** Mobile Carousel */}
         
            
            <Carousel className="flex md:hidden mt-16 w-full"
                setApi={setApi}
            opts={{
                align: "center",
                loop: true
            }}
            plugins={[
                Autoplay({
                delay: 4000,
                }),
            ]}
            >
                <CarouselContent className="-ml-1">
                    {reviews.map((review, index) => (
                    <CarouselItem key={index} className="pl-1 basis-3/4">
                        <div className="p-1">
                        <div className={cn(
                            "flex rounded-xl shrink",
                            current === index ? "text-foreground bg-background border border-[oklch(15.5%_0.025_152.7_/_0.2)] shadow-xl": "text-background border border-background",
                        )}>
                            <div className="flex flex-col justify-between aspect-auto  items-center p-6">
                                {/* Google Logo and Stars */}
                                    <div className="flex flex-col items-start gap-0 mb-12 w-full">
                                        <div className="flex">
                                            
                                                <span className={cn(current === index  ? "text-cta": "text-white/60", " text-sm")}>★</span>
                                                <span className={cn(current === index  ? "text-cta": "text-white/60", " text-sm")}>★</span>
                                                <span className={cn(current === index  ? "text-cta": "text-white/60", " text-sm")}>★</span>
                                                <span className={cn(current === index  ? "text-cta": "text-white/60", " text-sm")}>★</span>
                                                <span className={cn(current === index  ? "text-cta": "text-white/60", " text-sm")}>★</span>
                                            
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <span className="text-h5 font-semibold">Google</span>
                                        </div>
                                    
                                    </div>
                                    
                                    {/* Review Text */}
                                    <p className="text-small mb-4 leading-relaxed h-full">
                                        {review.review}
                                    </p>
                                    
                                    {/* Customer Name */}
                                    <div className={cn(current === index? "w-full border-foreground/20": "w-full border-background/20",  "border-t  pt-3")}>
                                        <p className="font-semibold">
                                            {review.name}
                                        </p>
                                    </div>
                            </div>
                        </div>
                        </div>
                    </CarouselItem>
                    ))}
                </CarouselContent>
        
            </Carousel>
            

            
            {/** Desktop Grid */}
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-6 max-w-screen-2xl mx-auto mt-16">
                {reviews.map((review, index) => (
                    <motion.div 
                        key={index}
                        className={cn(
                            "flex flex-col justify-between items-center w-full h-full rounded-xl",
                            index ===  0 ? "text-foreground bg-background border border-[oklch(15.5%_0.025_152.7_/_0.2)] relative px-6 pt-8 pb-6 shadow-xl": "text-background border border-background px-6 pt-8 pb-6 ",
                            
                        )}
                        whileInView={{
                            rotate: index ===  0 ? "2.5deg": "",
                            y: index ===  0 ? "-1rem": "",
                            x: index ===  0 ? "-0.5rem": "",
                            transition: {
                                duration: 0.3,
                                delay: 0.2
                            }
                        }}
                    >
                        {/* Google Logo and Stars */}
                        <div className="flex flex-col items-start gap-0 mb-12 w-full">
                             <div className="flex">
                                {[...Array(5)].map((_, i) => (
                                    <span key={i} className={cn(index ===  0  ? "text-cta": "text-white/60", " text-sm")}>★</span>
                                ))}
                            </div>
                            <div className="flex items-center gap-1">
                                <span className="text-h5 font-semibold">Google</span>
                            </div>
                           
                        </div>
                        
                        {/* Review Text */}
                        <p className="text-small mb-4 leading-relaxed h-full">
                            {review.review}
                        </p>
                        
                        {/* Customer Name */}
                        <div className={cn(index === 0? "w-full border-foreground/20": "w-full border-background/20",  "border-t  pt-3")}>
                            <p className="font-semibold">
                                {review.name}
                            </p>
                        </div>
                    </motion.div>
                ))}
            </div>
        </section>
    )
}

const reviews = [
    {
        name: "Ricardo Samuel",
        review: "The customer service is friendly, very professional and competent. The team are very reliable, honest and operated with integrity. You will not be disappointed, great service, very knowledgeable and the nicest people. Highly recommended!"
    },
    {
        name: "Lynsey Head",
        review: "The level of professionalism and attention to detail was evident throughout the process. I appreciated that every step was handled with care, Their commitment to excellence and customer satisfaction truly sets them apart."
    },
    {
        name: " Jason Haywood-Gordon",
        review: "Absolute pleasure to deal with, super friendly and highly professional but most importantly in this particular industry, reliable and trustworthy. I couldn’t recommend them highly enough, it’s a five star review from me without hesitation."
    },
    {
        name: "P J",
        review: "I had the pleasure of visiting their Canary Wharf location after hearing glowing recommendations, and it’s easy to see why they’ve built such an exceptional reputation. The attention to detail is second to none. Nothing ever feels rushed or transactional – it’s clear they take immense pride in what they do."
    },
    {
        name: "Mr M",
        review: "I can’t thank them enough for their outstanding customer service!! They were really patient with me and answered all of my questions. They are really knowledgable and make you feel at ease"
    },
    {
        name: "Alex Lambis",
        review: "Their expertise and dedication to customer service made what could have been a stressful search a genuinely enjoyable experience. They truly went above and beyond to help me"
    }
]
